import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { adminLogout } from "@/services/adminAuth";
import { ADMIN_ME_KEY } from "@/hooks/useAdminMe";
import type { AdminUserProfile } from "@/types/adminAuth";
import Button from "@/components/ui/Button";

/**
 * Minimal chrome for the SEPARATE admin realm — deliberately NOT the customer
 * app shell (no customer nav / app switcher). Just a title bar + the signed-in
 * admin + a logout that clears the admin session.
 */
export default function AdminShell({
  admin,
  children,
}: {
  admin: AdminUserProfile;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleLogout = async () => {
    try {
      await adminLogout();
    } finally {
      queryClient.removeQueries({ queryKey: ADMIN_ME_KEY });
      navigate("/admin/login", { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3">
        <span className="text-sm font-semibold text-gray-900">
          ASOLOOP Admin
        </span>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span className="tabular-nums">{admin.email}</span>
          <Button variant="secondary" size="compact" onClick={handleLogout}>
            Log out
          </Button>
        </div>
      </header>
      <main>{children}</main>
    </div>
  );
}
